import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { proxyService } from '../services/proxyService';
import { isSuperAdminUser } from '../utils/superAdmin';
import {
  isProxyExemptPath,
  isProxyOptionalPath,
  isProxyRequiredPath,
  PROXY_DISCONNECTED_MESSAGE,
} from '../utils/proxyConnectionPaths';

const PROXY_RECHECK_INTERVAL_MS = 30_000;

interface RefreshProxyStatusOptions {
  /** When true, re-check without toggling isChecking or clearing an established connection on transient failure. */
  background?: boolean;
}

interface ProxyConnectionContextType {
  isConnected: boolean;
  isChecking: boolean;
  /** False until the first foreground proxy check completes (login / launch). */
  initialCheckComplete: boolean;
  disconnectedMessage: string;
  refreshProxyStatus: (options?: RefreshProxyStatusOptions) => Promise<boolean>;
  handleProxyProtectedNavigation: (path: string, onAllowed?: () => void) => void;
  notifyProxyDisconnected: () => void;
}

const ProxyConnectionContext = createContext<ProxyConnectionContextType | undefined>(undefined);

export const useProxyConnection = () => {
  const context = useContext(ProxyConnectionContext);
  if (!context) {
    throw new Error('useProxyConnection must be used within ProxyConnectionProvider');
  }
  return context;
};

export const ProxyConnectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = isSuperAdminUser(user);
  const [isConnected, setIsConnected] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showReconnectNotice, setShowReconnectNotice] = useState(false);
  const [initialCheckComplete, setInitialCheckComplete] = useState(false);
  const wasConnectedRef = useRef(false);
  const redirectInFlightRef = useRef(false);
  const disconnectVerifyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disconnectFailuresRef = useRef(0);

  const refreshProxyStatus = useCallback(async (options?: RefreshProxyStatusOptions): Promise<boolean> => {
    const background = options?.background ?? false;
    if (!isAuthenticated || isSuperAdmin) {
      setIsConnected(true);
      setIsChecking(false);
      setInitialCheckComplete(true);
      return true;
    }
    if (!background) {
      setIsChecking(true);
    }
    try {
      const ok = await proxyService.checkProxyStatus();
      if (ok) {
        setIsConnected(true);
      } else if (!background || !wasConnectedRef.current) {
        setIsConnected(false);
      }
      return ok;
    } catch {
      if (!background || !wasConnectedRef.current) {
        setIsConnected(false);
      }
      return false;
    } finally {
      if (!background) {
        setIsChecking(false);
        setInitialCheckComplete(true);
      }
    }
  }, [isAuthenticated, isSuperAdmin]);

  const notifyProxyDisconnected = useCallback(() => {
    setIsConnected(false);
    if (wasConnectedRef.current) {
      setShowReconnectNotice(true);
    }
  }, []);

  const redirectToProxySettings = useCallback(
    (replace = true) => {
      if (redirectInFlightRef.current) return;
      if (isProxyExemptPath(location.pathname)) return;
      redirectInFlightRef.current = true;
      navigate('/proxy-settings', { replace });
      window.setTimeout(() => {
        redirectInFlightRef.current = false;
      }, 300);
    },
    [location.pathname, navigate]
  );

  const handleProxyProtectedNavigation = useCallback(
    (path: string, onAllowed?: () => void) => {
      if (isSuperAdmin || isConnected || isChecking) {
        if (onAllowed) onAllowed();
        else navigate(path);
        return;
      }

      const targetPath = path.split('?')[0];
      if (isProxyExemptPath(targetPath) || isProxyOptionalPath(targetPath)) {
        if (onAllowed) onAllowed();
        else navigate(path);
        return;
      }

      if (isProxyRequiredPath(targetPath)) {
        setShowReconnectNotice(true);
        redirectToProxySettings(true);
        return;
      }

      if (onAllowed) onAllowed();
      else navigate(path);
    },
    [isSuperAdmin, isConnected, isChecking, navigate, redirectToProxySettings]
  );

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || isSuperAdmin) {
      setIsConnected(true);
      setIsChecking(false);
      setInitialCheckComplete(true);
      wasConnectedRef.current = true;
      return;
    }

    void refreshProxyStatus();
  }, [authLoading, isAuthenticated, isSuperAdmin, refreshProxyStatus]);

  useEffect(() => {
    if (!isAuthenticated || isSuperAdmin) return;
    const unsubscribe = proxyService.subscribeToConnectionStatus((connected) => {
      if (connected) {
        if (disconnectVerifyTimerRef.current) {
          clearTimeout(disconnectVerifyTimerRef.current);
          disconnectVerifyTimerRef.current = null;
        }
        disconnectFailuresRef.current = 0;
        setIsConnected(true);
        return;
      }

      // One slow /health must not drop a live Windows agent. Only clear the connection
      // after two failed checks. Proxy Settings still runs a direct check and reconnects.
      if (disconnectVerifyTimerRef.current) return;
      disconnectVerifyTimerRef.current = setTimeout(() => {
        disconnectVerifyTimerRef.current = null;
        void (async () => {
          const ok = await proxyService.checkProxyStatus();
          if (ok) {
            disconnectFailuresRef.current = 0;
            setIsConnected(true);
            return;
          }
          disconnectFailuresRef.current += 1;
          if (!wasConnectedRef.current || disconnectFailuresRef.current >= 2) {
            setIsConnected(false);
            if (wasConnectedRef.current) {
              setShowReconnectNotice(true);
            }
          }
        })();
      }, 2500);
    });
    return () => {
      if (disconnectVerifyTimerRef.current) {
        clearTimeout(disconnectVerifyTimerRef.current);
        disconnectVerifyTimerRef.current = null;
      }
      unsubscribe();
    };
  }, [isAuthenticated, isSuperAdmin]);

  useEffect(() => {
    wasConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    if (!isAuthenticated || isSuperAdmin || isChecking || !initialCheckComplete) return;
    if (isConnected) return;
    if (isProxyExemptPath(location.pathname)) return;

    // Blocking proxy setup dialog handles disconnected state — no redirect to Proxy Settings.
  }, [
    isAuthenticated,
    isSuperAdmin,
    isChecking,
    initialCheckComplete,
    isConnected,
    location.pathname,
  ]);

  useEffect(() => {
    if (!isAuthenticated || isSuperAdmin || !initialCheckComplete) return;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshProxyStatus({ background: true });
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshProxyStatus({ background: true });
      }
    }, PROXY_RECHECK_INTERVAL_MS);

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, isSuperAdmin, initialCheckComplete, refreshProxyStatus]);

  const value = useMemo(
    () => ({
      isConnected,
      isChecking,
      initialCheckComplete,
      disconnectedMessage: PROXY_DISCONNECTED_MESSAGE,
      refreshProxyStatus,
      handleProxyProtectedNavigation,
      notifyProxyDisconnected,
    }),
    [
      isConnected,
      isChecking,
      initialCheckComplete,
      refreshProxyStatus,
      handleProxyProtectedNavigation,
      notifyProxyDisconnected,
    ]
  );

  const showDisconnectedBanner =
    isAuthenticated && !isSuperAdmin && !isChecking && !isConnected && !isProxyExemptPath(location.pathname);

  return (
    <ProxyConnectionContext.Provider value={value}>
      {showDisconnectedBanner && (
        <Alert
          severity="error"
          sx={{
            position: 'fixed',
            top: { xs: 8, md: 'calc(64px * var(--app-page-zoom, 1) + 8px)' },
            left: { xs: 8, md: 'calc(var(--app-sidebar-width, 208px) + 12px)' },
            right: 12,
            zIndex: (theme) => theme.zIndex.appBar + 2,
            borderRadius: 1,
            alignItems: 'flex-start',
            '& .MuiAlert-message': {
              overflow: 'visible',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          }}
        >
          {PROXY_DISCONNECTED_MESSAGE}
        </Alert>
      )}
      {children}
      <Snackbar
        open={showReconnectNotice}
        autoHideDuration={8000}
        onClose={() => setShowReconnectNotice(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          onClose={() => setShowReconnectNotice(false)}
          sx={{ width: '100%' }}
        >
          Proxy disconnected. Reconnect the CRUD Expert desktop agent on the Proxy Settings page to continue using Collections,
          Test Plans, and other proxy features.
        </Alert>
      </Snackbar>
    </ProxyConnectionContext.Provider>
  );
};
